import React, { useContext, useEffect, useRef, useState } from 'react'
import { SlArrowDown } from 'react-icons/sl'
import CursorContext from '../providers/Cursor'
import { AiOutlineClose } from 'react-icons/ai'
import { motion, AnimatePresence } from 'framer-motion'
import { NavLink, useLocation } from 'react-router-dom'
import PillLink from '../ui/cinematic/PillLink'
// Fondo del menú: el metaball del hero (en la home ya está en caché)
import menuVideo from '../../assets/home/hero-scrub.mp4'
import { stegaClean } from '@sanity/client/stega'
import { useAjustes } from '../../cms/ajustes'
import { elemento, useTextos } from '../../cms/textos'
import RESPALDO from '../../Data/Paginas/paginaComun.json'


// Las direcciones son fijas; los nombres salen de "Páginas → Menú, cierre y 404"
// (barra.paginas), que también usan el footer y la 404
export const RUTAS = {
    inicio: '/',
    nosotros: '/aboutus',
    servicios: '/services',
    portafolio: '/portafolio',
    analisis: '/analisis-360',
    blog: '/blog',
    contacto: '/contact',
}

/** [{ label, to }] de esas páginas, en ese orden, con los nombres editables. */
export function usePaginas(claves = Object.keys(RUTAS)) {
    const { paginas } = useTextos('paginaComun', RESPALDO).barra
    return claves.map((clave) => ({ label: paginas[clave], to: RUTAS[clave] }))
}

/**
 * En la vista previa del panel, un texto de Sanity trae al final caracteres
 * invisibles (stega) que dicen de qué campo viene. Si se parte letra por letra,
 * la marca se rompe y el texto pierde su recuadro de "editar con un clic": se
 * separa para dibujar las letras limpias y dejar la marca entera en un solo nodo.
 * Fuera de la vista previa la marca es ''.
 */
export function separarMarca(texto = '') {
    const original = String(texto)
    const limpio = stegaClean(original)
    return [limpio, original.startsWith(limpio) ? original.slice(limpio.length) : '']
}

const EASE_EXPO = [0.16, 1, 0.3, 1]

// El menú se abre como un círculo que crece desde el botón (arriba a la derecha)
const overlayVariants = {
    hidden: { clipPath: 'circle(0% at 95% 5%)' },
    visible: { clipPath: 'circle(150% at 95% 5%)', transition: { duration: 0.9, ease: EASE_EXPO } },
    exit: { clipPath: 'circle(0% at 95% 5%)', transition: { duration: 0.55, delay: 0.1, ease: [0.7, 0, 0.84, 0] } },
}

const listVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.06, delayChildren: 0.25 } },
}

// Cada link sube desde detrás de una máscara, como las líneas del titular del hero
const lineVariants = {
    hidden: { y: '110%' },
    visible: { y: '0%', transition: { duration: 0.9, ease: EASE_EXPO } },
    exit: { y: '110%', transition: { duration: 0.3, ease: 'easeIn' } },
}

const fadeVariants = {
    hidden: { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, delay: 0.55, ease: EASE_EXPO } },
    exit: { opacity: 0, transition: { duration: 0.2 } },
}


// Las páginas de la píldora central de la barra
const ACCESOS = ['nosotros', 'servicios', 'portafolio', 'blog']

/**
 * Barra flotante del rediseño cinemático: píldoras de
 * vidrio esmerilado sobre el contenido, sin franja de fondo. El menú de
 * pantalla completa es el mismo de la barra clásica.
 */
function FloatingNavbar() {
    const { agenda } = useAjustes();
    const textos = useTextos('paginaComun', RESPALDO).barra
    const quickLinks = usePaginas(ACCESOS)
    const [openMenu, setOpenMenu] = useState(false)
    const { navBarEnter, navBarExit, selectEnter } = useContext(CursorContext)
    const pill = 'pointer-events-auto rounded-full bg-ink/50 ring-1 ring-inset ring-cream/10 backdrop-blur-md'

    return (
        <header className='fixed inset-x-0 top-0 z-index-99 pointer-events-none px-4 pt-4 md:px-8 md:pt-6'>
            {/* Grilla 1fr-auto-1fr: con justify-between la píldora central quedaba
                corrida hacia el logo, que es más angosto que los botones de la derecha */}
            <div className='grid grid-cols-[1fr_auto_1fr] items-center gap-4'>
                <NavLink to='/' aria-label='Awna — inicio' onMouseEnter={navBarEnter} onMouseLeave={navBarExit} className={`${pill} flex h-12 items-center justify-self-start px-5`}>
                    <img src='https://awna.s3.sa-east-1.amazonaws.com/LogoWhite.png' className='h-6 w-auto' alt='Awna' />
                </NavLink>

                <nav aria-label='Principal' className={`${pill} hidden items-center gap-1 p-1 lg:flex`}>
                    {quickLinks.map(({ label, to }) => (
                        <NavLink
                            key={to}
                            to={to}
                            onMouseEnter={navBarEnter}
                            onMouseLeave={navBarExit}
                            // La página actual va rellena, como en la referencia. NavLink también
                            // marca las rutas hijas: /services/:id activa Servicios y /blog/... Blog.
                            className={({ isActive }) => `rounded-full px-4 py-2 text-sm transition-colors duration-300 ${isActive
                                ? 'bg-cream text-ink'
                                : 'text-cream/70 hover:bg-cream/10 hover:text-cream'}`}
                        >
                            {label}
                        </NavLink>
                    ))}
                </nav>

                <div className='pointer-events-auto col-start-3 flex items-center gap-2 justify-self-end'>
                    <a
                        href={agenda}
                        target='_blank'
                        rel='noopener noreferrer'
                        onMouseEnter={selectEnter}
                        onMouseLeave={navBarExit}
                        className='hidden h-12 items-center rounded-full bg-cream px-5 text-sm font-medium text-ink shadow-[0_4px_24px_rgba(10,10,10,0.18)] ring-1 ring-inset ring-ink/10 transition-colors duration-300 hover:bg-white md:inline-flex'
                    >
                        {textos.agendar}
                    </a>
                    <button
                        type='button'
                        onClick={() => setOpenMenu((v) => !v)}
                        onMouseEnter={navBarEnter}
                        onMouseLeave={navBarExit}
                        aria-expanded={openMenu}
                        aria-label={openMenu ? 'Cerrar menú' : 'Abrir menú'}
                        className={`${pill} z-index-999999 relative flex h-12 items-center gap-2 px-5 text-sm text-cream`}
                    >
                        {openMenu ? <AiOutlineClose size={16} /> : <SlArrowDown size={12} />}
                        {openMenu ? textos.cerrar : textos.menu}
                    </button>
                </div>
            </div>
            <div className='pointer-events-auto'>
                <MenuOverlay open={openMenu} onClose={() => setOpenMenu(false)} />
            </div>
        </header>
    )
}

/**
 * Texto que "rueda" en hover: cada letra sube y la reemplaza una copia que
 * entra desde abajo, en cascada (misma fuente: el cambio a serif se veía mal).
 * Las dos capas son decorativas; el nombre accesible lo da el aria-label del link.
 */
export function TextoRoll({ texto, className = '' }) {
    const [limpio, marca] = separarMarca(texto)
    const capa = (entrante) => [...limpio].map((letra, i) => (
        <span
            key={i}
            style={{ transitionDelay: `${i * 18}ms` }}
            className={`inline-block transition-transform duration-500 ease-expo ${entrante
                ? 'translate-y-[115%] group-hover/link:translate-y-0 group-focus-visible/link:translate-y-0'
                : 'group-hover/link:-translate-y-[115%] group-focus-visible/link:-translate-y-[115%]'}`}
        >
            {letra === ' ' ? ' ' : letra}
        </span>
    ))

    return (
        <span aria-hidden='true' className={`relative block overflow-hidden pb-[0.12em] ${className}`}>
            <span className='block'>{capa(false)}</span>
            <span className='absolute inset-0 block'>{capa(true)}</span>
            {marca}
        </span>
    )
}

function MenuOverlay({ open, onClose }) {
    const { agenda, correo, redes } = useAjustes();
    const textos = useTextos('paginaComun', RESPALDO).barra
    const menuLinks = usePaginas()
    const Bajada = elemento(textos, 'bajada', 'p')
    const EtiquetaCorreo = elemento(textos, 'etiquetaCorreo', 'span')
    const { selectEnter, navBarEnter, navBarExit } = useContext(CursorContext)
    const location = useLocation()
    const panelRef = useRef(null)
    // En un ref: onClose llega como función nueva en cada render y el efecto
    // de abajo no debe repetirse por eso (devolvería el foco a cada rato)
    const onCloseRef = useRef(onClose)
    onCloseRef.current = onClose

    // Mientras está abierto: Escape cierra, la página de atrás no se scrollea y
    // el foco entra al menú (y vuelve al botón que lo abrió al cerrarse)
    useEffect(() => {
        if (!open) return undefined
        const panel = panelRef.current
        const previo = document.activeElement
        const html = document.documentElement
        const overflowPrevio = html.style.overflow
        html.style.overflow = 'hidden'

        const onKey = (e) => { if (e.key === 'Escape') onCloseRef.current() }
        // SmoothScroll.js escucha la rueda en window: sin esto movería la página de atrás
        const frenarRueda = (e) => e.stopPropagation()
        window.addEventListener('keydown', onKey)
        panel?.addEventListener('wheel', frenarRueda)
        panel?.querySelector('a')?.focus({ preventScroll: true })

        return () => {
            html.style.overflow = overflowPrevio
            window.removeEventListener('keydown', onKey)
            panel?.removeEventListener('wheel', frenarRueda)
            if (previo && typeof previo.focus === 'function') previo.focus({ preventScroll: true })
        }
    }, [open])

    return (
        <AnimatePresence>
            {open &&
                <motion.div
                    ref={panelRef}
                    key='menu'
                    role='dialog'
                    aria-modal='true'
                    aria-label='Menú'
                    variants={overlayVariants}
                    initial='hidden'
                    animate='visible'
                    exit='exit'
                    onMouseEnter={navBarEnter}
                    onMouseLeave={navBarExit}
                    // Alto y ancho explícitos (no inset-0): en la barra clásica el backdrop-blur la
                    // vuelve el contenedor de los fixed y el menú medía lo mismo que ella, 80 px.
                    // dvh y no svh: en Safari iOS svh es el alto con las barras desplegadas y,
                    // al esconderse la barra inferior, abajo quedaba asomando la página.
                    className='fixed left-0 top-0 h-screen h-dvh w-full overflow-y-auto overflow-x-hidden overscroll-contain bg-ink text-cream'
                >
                    {/* El metaball en loop, sin overlay: el clip ya es oscuro */}
                    <video src={menuVideo} autoPlay muted loop playsInline aria-hidden='true' className='pointer-events-none absolute inset-0 h-full w-full object-cover opacity-50' />

                    <div className='container relative flex min-h-full flex-col px-5 pb-8 pt-28 md:px-10 2xl:px-0'>
                        <div className='grid flex-1 grid-cols-1 gap-12 lg:grid-cols-12'>
                            {/* Links grandes: en hover pasan a la serif itálica y el resto se apaga */}
                            <motion.nav
                                aria-label='Menú principal'
                                variants={listVariants}
                                className='group/menu flex flex-col lg:order-2 lg:col-span-8 lg:items-end'
                            >
                                {menuLinks.map(({ label, to }, i) => {
                                    const isActive = location.pathname === to
                                    return (
                                        <span key={to} className='block overflow-hidden'>
                                            <motion.span variants={lineVariants} className='block'>
                                                <NavLink
                                                    to={to}
                                                    end={to === '/'}
                                                    onClick={onClose}
                                                    onMouseEnter={selectEnter}
                                                    onMouseLeave={navBarEnter}
                                                    aria-label={stegaClean(label)}
                                                    className={`group/link flex items-baseline gap-4 py-1 transition-opacity duration-500 focus-visible:outline-none lg:flex-row-reverse lg:group-hover/menu:opacity-30 lg:hover:!opacity-100 ${isActive ? '' : 'opacity-70'}`}
                                                >
                                                    <span aria-hidden='true' className='text-sm tabular-nums text-cream/40'>{String(i + 1).padStart(2, '0')}</span>
                                                    {/* Desde md el tamaño depende también del alto: con text-8xl los 7 links
                                                        no cabían en pantallas bajas y el panel hacía scroll */}
                                                    <TextoRoll texto={label} className='text-5xl font-medium leading-[1.05] tracking-tight md:text-[min(7vw,7.5vh)]' />
                                                    {isActive && <span aria-hidden='true' className='h-2 w-2 shrink-0 self-center rounded-full bg-cream md:h-3 md:w-3' />}
                                                </NavLink>
                                            </motion.span>
                                        </span>
                                    )
                                })}
                            </motion.nav>

                            {/* Bajada y contacto directo */}
                            <motion.div variants={fadeVariants} className='flex flex-col justify-between gap-10 lg:order-1 lg:col-span-4'>
                                <Bajada className='hidden max-w-xs text-lg leading-relaxed text-cream/60 lg:block'>
                                    {textos.bajada}
                                </Bajada>
                                <div className='flex flex-col items-start gap-6'>
                                    <a href={`mailto:${correo}`} onMouseEnter={selectEnter} onMouseLeave={navBarEnter} className='group flex flex-col gap-1'>
                                        <EtiquetaCorreo className='text-xs uppercase tracking-[0.25em] text-cream/40'>{textos.etiquetaCorreo}</EtiquetaCorreo>
                                        <span className='text-2xl text-cream/80 transition-colors duration-300 group-hover:text-cream'>{correo}</span>
                                    </a>
                                    <PillLink href={agenda} external>{textos.agendar}</PillLink>
                                </div>
                            </motion.div>
                        </div>

                        <motion.div variants={fadeVariants} className='mt-12 flex flex-col gap-4 border-t border-cream/15 pt-6 text-sm md:mt-8 md:flex-row md:items-center md:justify-between'>
                            <ul className='flex flex-wrap gap-x-6 gap-y-2'>
                                {redes.map(({ name, url }) => (
                                    <li key={name}>
                                        <a href={url} target='_blank' rel='noopener noreferrer' onMouseEnter={selectEnter} onMouseLeave={navBarEnter} className='text-cream/60 transition-colors duration-300 hover:text-cream'>
                                            {name} <span aria-hidden='true'>↗</span>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                            <p className='text-cream/40'>© {new Date().getFullYear()} Awna</p>
                        </motion.div>
                    </div>
                </motion.div>
            }
        </AnimatePresence>
    )
}

/** La barra del sitio: la flotante en todas las páginas (la clásica se retiró). */
const Navbar = FloatingNavbar

export default Navbar
